import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  booleanAttribute,
} from '@angular/core';

export type FormActionsLayout = 'page' | 'dialog' | 'inline';
export type FormActionsCancelVariant = 'secondary' | 'outline-secondary' | 'outline-acx';
export type FormActionsSaveVariant = 'primary' | 'danger' | 'outline-secondary' | 'outline-acx';
export type FormActionsAlign = 'end' | 'between';

@Component({
  selector: 'cx-form-actions',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './form-actions.component.html',
  styleUrls: ['./form-actions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormActionsComponent {
  /** Page: sticky bottom-right. Dialog: p-dialog footer. Inline: embedded panel without sticky. */
  @Input() layout: FormActionsLayout = 'page';

  @Input({ transform: booleanAttribute }) showCancel = true;
  @Input({ transform: booleanAttribute }) showSave = true;
  @Input() cancelLabel = '';
  @Input() saveLabel = '';
  @Input() cancelVariant: FormActionsCancelVariant = 'secondary';
  @Input() saveVariant: FormActionsSaveVariant = 'primary';
  @Input() actionsAlign: FormActionsAlign = 'end';
  @Input({ transform: booleanAttribute }) disabled = false;
  @Input({ transform: booleanAttribute }) isSubmitting = false;
  @Input() saveType: 'submit' | 'button' = 'submit';
  /** Overrides dialog default button size when set. */
  @Input({ transform: booleanAttribute }) small?: boolean;
  /** Associates submit button with a form when actions render outside the form element. */
  @Input() formId?: string;

  @Output() cancel = new EventEmitter<void>();
  @Output() save = new EventEmitter<void>();

  get sticky(): boolean {
    return this.layout === 'page';
  }

  get useSmallButtons(): boolean {
    return this.small ?? this.layout === 'dialog';
  }

  get cancelButtonClass(): string {
    switch (this.cancelVariant) {
      case 'outline-secondary':
        return 'btn-outline-secondary';
      case 'outline-acx':
        return 'btn-outline-acx';
      default:
        return 'btn-secondary';
    }
  }

  get saveButtonClass(): string {
    switch (this.saveVariant) {
      case 'danger':
        return 'btn-danger';
      case 'outline-secondary':
        return 'btn-outline-secondary';
      case 'outline-acx':
        return 'btn-outline-acx';
      default:
        return 'btn-primary';
    }
  }

  onSaveClick(): void {
    if (this.saveType === 'button') {
      this.save.emit();
    }
  }
}
